/**
 * Product Form Dialog - Create and Edit Products
 * Updated: Includes beneficiamento fields (tamanho, peso, preços)
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { productSchema, ProductFormData } from "@/schemas/productSchema";
import { CategoryCombobox } from "./CategoryCombobox";
import { UnitCombobox } from "./UnitCombobox";
import { Product } from "@/types/product";
import { productsAdapter, categoriesAdapter, unitsAdapter } from "@/data";
import { Loader2, Info, Scale, Droplet, Package } from "lucide-react";
import { format } from "date-fns";

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  product?: Product | null;
  onSubmit: (data: ProductFormData) => Promise<void>;
  isSubmitting: boolean;
}

export function ProductFormDialog({
  open,
  onOpenChange,
  mode,
  product,
  onSubmit,
  isSubmitting,
}: ProductFormDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: "",
      category: "",
      unidadePadrao: "unidade",
      perishable: false,
      controlLot: false,
      controlExpiry: false,
      minQuantity: 0,
      notes: "",
    },
  });

  const perishable = watch("perishable");
  const controlLot = watch("controlLot");
  const controlExpiry = watch("controlExpiry");

  // Lógica condicional: perecível força controle de lote e validade
  useEffect(() => {
    if (perishable) {
      setValue("controlLot", true);
      setValue("controlExpiry", true);
    }
  }, [perishable, setValue]);

  // Quando abre o diálogo em modo edição, preencher o formulário
  useEffect(() => {
    if (open) {
      if (mode === "edit" && product) {
        reset({
          name: product.nome,
          category: product.id_categoria,
          unidadePadrao: product.unidade_padrao,
          perishable: product.perecivel,
          controlLot: product.controla_lote,
          controlExpiry: product.controla_validade,
          minQuantity: product.quantidade_minima || 0,
          notes: product.observacoes || "",
        });
      } else {
        reset({
          name: "",
          category: "",
          unidadePadrao: "unidade",
          perishable: false,
          controlLot: false,
          controlExpiry: false,
          minQuantity: 0,
          notes: "",
        });
      }
    }
  }, [open, mode, product, reset]);

  const onFormSubmit = async (data: ProductFormData) => {
    await onSubmit(data);
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle>
            {mode === "create" ? "Novo Produto" : "Editar Produto"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Preencha os dados para criar um novo produto."
              : "Atualize as informações do produto."}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
        <form id="product-form" onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* SEÇÃO: Dados Principais */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Dados Principais</h3>
              <Separator className="flex-1" />
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome do produto *</Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="Leite Condensado Italac 395 g"
                disabled={isSubmitting}
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            {/* Categoria e Unidade Padrão */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoria *</Label>
                <CategoryCombobox
                  value={watch("category")}
                  onChange={(value) => setValue("category", value, { shouldValidate: true })}
                  disabled={isSubmitting}
                  error={!!errors.category}
                />
                {errors.category && (
                  <p className="text-sm text-destructive">{errors.category.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="unidadePadrao">Unidade Padrão *</Label>
                <Select
                  value={watch("unidadePadrao")}
                  onValueChange={(value) => setValue("unidadePadrao", value as any, { shouldValidate: true })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className={errors.unidadePadrao ? "border-destructive" : ""}>
                    <SelectValue placeholder="Selecione a unidade..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peso_g">
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4" />
                        <span>Peso (g)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="volume_ml">
                      <div className="flex items-center gap-2">
                        <Droplet className="w-4 h-4" />
                        <span>Volume (ml)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="unidade">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span>Unidade</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.unidadePadrao && (
                  <p className="text-sm text-destructive">{errors.unidadePadrao.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  💡 Como este produto é normalmente medido
                </p>
              </div>
            </div>
          </div>

          {/* SEÇÃO: Configurações de Controle */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Configurações de Controle</h3>
              <Separator className="flex-1" />
            </div>

            {/* Perecível */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="perishable">Produto perecível</Label>
                <p className="text-sm text-muted-foreground">
                  Se marcado, ativa controle de lote e validade
                </p>
              </div>
              <Checkbox
                id="perishable"
                checked={perishable}
                onCheckedChange={(checked) => setValue("perishable", checked as boolean)}
                disabled={isSubmitting}
              />
            </div>

            {/* Controla lote */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="controlLot">Controla lote</Label>
                <p className="text-sm text-muted-foreground">
                  {perishable ? "Obrigatório para perecíveis" : "Rastreamento por lote"}
                </p>
              </div>
              <Checkbox
                id="controlLot"
                checked={controlLot}
                onCheckedChange={(checked) => setValue("controlLot", checked as boolean)}
                disabled={isSubmitting || perishable}
              />
            </div>

            {/* Controla validade */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="controlExpiry">Controla validade</Label>
                <p className="text-sm text-muted-foreground">
                  {perishable ? "Obrigatório para perecíveis" : "Rastreamento de vencimento"}
                </p>
              </div>
              <Checkbox
                id="controlExpiry"
                checked={controlExpiry}
                onCheckedChange={(checked) => setValue("controlExpiry", checked as boolean)}
                disabled={isSubmitting || perishable}
              />
            </div>

            {/* Quantidade Mínima */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="minQuantity">Quantidade Mínima em Estoque</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Define o nível mínimo de estoque. Quando a quantidade atual 
                        ficar abaixo deste valor, será exibido um alerta de reposição.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="minQuantity"
                type="number"
                step="1"
                min="0"
                {...register("minQuantity", { 
                  setValueAs: (v) => v === "" ? 0 : parseFloat(v) 
                })}
                placeholder="0"
                disabled={isSubmitting}
                className={errors.minQuantity ? "border-destructive" : ""}
              />
              {errors.minQuantity && (
                <p className="text-sm text-destructive">{errors.minQuantity.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Valor em unidade base
              </p>
            </div>
          </div>

          {/* SEÇÃO: Observações */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Observações</h3>
              <Separator className="flex-1" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Observações (opcional)</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Usado nas receitas de pudim tradicional e diet."
                rows={3}
                disabled={isSubmitting}
                maxLength={500}
              />
              {errors.notes && (
                <p className="text-sm text-destructive">{errors.notes.message}</p>
              )}
              <p className="text-xs text-muted-foreground text-right">
                {watch("notes")?.length || 0}/500 caracteres
              </p>
            </div>
          </div>

          {/* Campos Audit (somente no modo edição) */}
          {mode === "edit" && product && (
            <div className="space-y-2 pt-4 border-t">
              <div className="text-xs text-muted-foreground space-y-1">
                <p>📅 Criado em {format(new Date(product.created_at), "dd/MM/yyyy 'às' HH:mm")}</p>
                <p>👤 Criado por {product.created_by}</p>
                {product.updated_at && (
                  <>
                    <p>📅 Última atualização em {format(new Date(product.updated_at), "dd/MM/yyyy 'às' HH:mm")}</p>
                    {product.updated_by && <p>👤 Atualizado por {product.updated_by}</p>}
                  </>
                )}
              </div>
            </div>
          )}

        </form>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button type="submit" form="product-form" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === "create" ? "Salvar produto" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
